import SimpleBar from "simplebar-react";
import classNames from "classnames";
import AsyncSelect from "react-select/async";
import { isEmpty } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Form, FormGroup, Modal, ModalBody } from "reactstrap";

import SiteTitle from "./../../../helpers/SiteTitle/SiteTitle";
import Icon from "./../../../components/Icon/Icon";
import Button from "../../../components/Button/Button";
import PaginationComponent from "./../../../components/Pagination/Pagination";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import ShowDetails from "./../../../components/ShowDetails/ShowDetails";
import TableFilter from "../../../components/TableFilter/TableFilter";
import SearchTable from "../../../components/SearchTable/SearchTable";
import PageHeader from "../../../components/PageHeader/PageHeader";
import { DataTable } from "./../../../components/DataTable/DataTable";
import { Block } from "../../../components/Block/Block";
import { Col, Row } from "../../../components/Grid/Grid";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { ticketUserContext } from "../../../context/UserContext/TicketUserContext/TicketUserContext";
import { convertDate, convertRoleToText } from "../../../utils";
import { hostname } from "../../../services/configServices/config";
import {
  convertTicketTypeToColor,
  convertTicketTypeToText,
  ticketTypeItems,
} from "../../../utils/enum";
import {
  createTicketForAdminValidation,
  createTicketValidation,
} from "../../../utils/validation/validation";

const TicketsUser = () => {
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);
  const loader = useSelector((state) => state.loader);

  const {
    tickets,
    ticket,
    setTicket,
    status,
    setStatus,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    setIsShowModal,
    mobileView,
    setMobileView,
    handleGetUsers,
    handleSendTicketMessage,
    handleCloseTicket,
    handleGetTickets,
    handleGetTicket,
    handleDeleteTicket,
    handleCreateTicket,
    handleUploadFile,
  } = useContext(ticketUserContext);

  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    handleGetTickets();
  }, [start, end, pageNumber, itemsCount, status, sort]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      auth?.isAdmin ? createTicketForAdminValidation : createTicketValidation
    ),
  });

  const chatBodyClass = classNames({
    "nk-msg-body": true,
    "bg-white": true,
    "show-message": mobileView,
  });

  const ticketInfo = (
    <ul className="nk-msg-tags">
      <li>
        <div className="mb-2">
          <ShowDetails
            showDetails={showDetails}
            setShowDetails={setShowDetails}
          />
        </div>
      </li>
      <li>
        <span className="label-tag">
          <Icon name="user-fill" />{" "}
          <InfoItem title="کاربر">
            {ticket?.user?.firstName} {ticket?.user?.lastName}
          </InfoItem>
        </span>
      </li>
      <li>
        <span className="label-tag">
          <Icon name="user-fill" />{" "}
          <InfoItem title="شماره تماس">{ticket?.user?.phoneNumber}</InfoItem>
        </span>
      </li>
      {showDetails && (
        <>
          <li>
            <span className="label-tag">
              <Icon name="user-fill" />{" "}
              <InfoItem title="پست الکترونیک">{ticket?.user?.email}</InfoItem>
            </span>
          </li>
          <li>
            <span className="label-tag">
              <Icon name="user-fill" />{" "}
              <InfoItem title="نقش">
                {convertRoleToText(ticket?.user?.role)}
              </InfoItem>
            </span>
          </li>
          <li>
            <span className="label-tag">
              <Icon name="mobile" />{" "}
              <InfoItem title="کد ملی">{ticket?.user?.nationalNumber}</InfoItem>
            </span>
          </li>
          <li>
            <span className="label-tag">
              <Icon name="blank" />{" "}
              <InfoItem title="وضعیت کاربر">
                <span
                  className={`badge badge-dot has-bg badge-${
                    ticket?.user?.isActive ? "success" : "danger"
                  }`}
                >
                  {ticket?.user?.isActive ? "فعال" : "غیرفعال"}
                </span>
              </InfoItem>
            </span>
          </li>
          {!isEmpty(ticket?.user?.agent) && (
            <>
              <li>
                <span className="label-tag">
                  <Icon name="user-fill" />{" "}
                  <InfoItem title="نماینده">
                    {ticket?.user?.agent?.firstName}{" "}
                    {ticket?.user?.agent?.lastName}
                  </InfoItem>
                </span>
              </li>
              <li>
                <span className="label-tag">
                  <Icon name="user-fill" />{" "}
                  <InfoItem title="کد ملی نماینده">
                    {ticket?.user?.agent?.nationalNumber}
                  </InfoItem>
                </span>
              </li>
              <li>
                <span className="label-tag">
                  <Icon name="user-fill" />{" "}
                  <InfoItem title="شماره تماس نماینده">
                    {ticket?.user?.agent?.phoneNumber}
                  </InfoItem>
                </span>
              </li>
            </>
          )}
          <li>
            <span className="label-tag">
              <Icon name="clock-fill" />{" "}
              <InfoItem title="ایجاد شده">
                {convertDate(ticket.createdAt, "DD MMM YYYY - HH:mm")}
              </InfoItem>
            </span>
          </li>
          <li>
            <span className="label-tag">
              <Icon name="clock-fill" />{" "}
              <InfoItem title="ویرایش شده">
                {convertDate(ticket.updatedAt, "DD MMM YYYY - HH:mm")}
              </InfoItem>
            </span>
          </li>
          <li>
            <span className="label-tag">
              <Icon name="flag-fill" />
              <InfoItem title="کد پیگیری">{ticket.trackingCode}</InfoItem>
            </span>
          </li>
        </>
      )}
    </ul>
  );

  return (
    <>
      <SiteTitle title="تیکت" />

      <PageHeader
        title="تیکت"
        description="با کلیک بر روی دکمه + شما میتوانید تیکتی را ارسال نمایید"
      />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setTicketStatus={setStatus}
        ticketStatus={status}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <div className="nk-msg">
        <div className="nk-msg-aside">
          <Block>
            <DataTable>
              <SearchTable
                firstItem={
                  <Button
                    color="primary"
                    className="btn-icon"
                    onClick={() => {
                      setIsShowModal(true);
                      reset();
                    }}
                  >
                    <Icon name="plus" />
                  </Button>
                }
                filterPlaceholder="عنوان، کد پیگیری و ..."
                filter={filter}
                setFilter={setFilter}
                onSearch={handleGetTickets}
              />
            </DataTable>
            <SimpleBar className="nk-msg-list">
              {isEmpty(tickets) ? (
                <div className="p-3">
                  <p className="text-center fw-bold">
                    تیکتی برای نمایش یافت نشد
                  </p>
                </div>
              ) : (
                <>
                  {tickets.map((item) => (
                    <div
                      className={`nk-msg-item ${
                        ticket._id === item._id ? "current" : ""
                      }`}
                      key={item._id}
                      onClick={() => handleGetTicket(item._id)}
                    >
                      {auth?.isAdmin ? (
                        <UserAvatar
                          {...(item?.user?.avatar
                            ? { image: item?.user?.avatar }
                            : {
                                icon: "user-alt",
                              })}
                        />
                      ) : (
                        <UserAvatar icon="comments" />
                      )}
                      <div className="nk-msg-info">
                        <div className="nk-msg-from">
                          <div className="nk-msg-sender">
                            <div className="name">
                              {convertDate(item.updatedAt, "DD MMM - HH:mm")}
                            </div>
                          </div>
                          <div className="nk-msg-meta">
                            <div
                              className={`badge badge-dot has-bg badge-${convertTicketTypeToColor(
                                item.status
                              )}`}
                            >
                              {convertTicketTypeToText(item.status)}
                            </div>
                          </div>
                        </div>
                        <div className="nk-msg-context">
                          <div className="nk-msg-text">
                            <h6 className="title">{item?.title}</h6>
                            {auth?.isAdmin ? (
                              <p>
                                {item?.user?.firstName} - {item?.user?.lastName}{" "}
                                - {item?.user?.phoneNumber}
                              </p>
                            ) : (
                              <p>{item.trackingCode}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {tickets.length > 0 && (
                    <div className="card-inner d-flex justify-content-center">
                      <PaginationComponent
                        pageCount={pageCount}
                        currentPage={pageNumber}
                        setPageNumber={setPageNumber}
                      />
                    </div>
                  )}
                </>
              )}
            </SimpleBar>
          </Block>
        </div>
        {!isEmpty(ticket) && (
          <div className={chatBodyClass}>
            <div className="nk-msg-head">
              <h4 className="title d-none d-lg-block">{ticket.title}</h4>
              <div className="nk-msg-head-meta">
                <div className="d-none d-lg-block">
                  {auth?.isAdmin ? (
                    ticketInfo
                  ) : (
                    <ul className="nk-msg-tags">
                      <li>
                        <span className="label-tag">
                          <Icon name="flag-fill" />{" "}
                          <span>{ticket.trackingCode}</span>
                        </span>
                      </li>
                    </ul>
                  )}
                </div>
                <div className="d-lg-none">
                  <Button
                    className="btn-icon btn-trigger nk-msg-hide ml-n1"
                    onClick={() => {
                      setTicket({});
                      setMobileView(false);
                    }}
                  >
                    <Icon name="arrow-left" />
                  </Button>
                </div>
                <ul className="text-right">
                  <li className="mb-1">
                    <span
                      className={`badge badge-dim badge-${convertTicketTypeToColor(
                        ticket.status
                      )} badge-sm`}
                    >
                      <span>{convertTicketTypeToText(ticket.status)}</span>
                    </span>
                  </li>

                  {ticket.status !== ticketTypeItems[4] && (
                    <li className="mb-1">
                      <Button
                        outline
                        size="sm"
                        color="light"
                        className="btn-dim"
                        onClick={() =>
                          areYouSureMessage(
                            handleCloseTicket,
                            `آیا از بستن درخواست ${ticket.title} مطمئنید؟`
                          )
                        }
                      >
                        <Icon name="check" />
                        <span>بستن درخواست</span>
                      </Button>
                    </li>
                  )}

                  <li>
                    <Button
                      outline
                      size="sm"
                      color="danger"
                      className="btn-dim"
                      onClick={() =>
                        areYouSureMessage(
                          handleDeleteTicket,
                          `آیا از حذف درخواست ${ticket.title} مطمئنید؟`
                        )
                      }
                    >
                      <Icon name="trash" />
                      <span>حذف</span>
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
            {/*nk-msg-head*/}
            <SimpleBar className="nk-msg-reply nk-reply">
              <div className="nk-msg-head py-4 d-lg-none">
                <h4 className="title">{ticket.title}</h4>
                {auth?.isAdmin ? (
                  ticketInfo
                ) : (
                  <ul className="nk-msg-tags">
                    <li>
                      <span className="label-tag">
                        <Icon name="flag-fill" />{" "}
                        <span>{ticket.trackingCode}</span>
                      </span>
                    </li>
                  </ul>
                )}
              </div>
              {!isEmpty(ticket.messages) &&
                ticket.messages.map((item) => (
                  <div className="nk-reply-item">
                    <div className="nk-reply-header">
                      <div className="user-card">
                        {item.user ? (
                          <UserAvatar
                            {...(item.user?.avatar
                              ? { image: item.user?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                        ) : (
                          <>
                            {auth?.isAdmin ? (
                              <UserAvatar
                                {...(ticket?.user?.avatar
                                  ? { image: ticket?.user?.avatar }
                                  : {
                                      icon: "user-alt",
                                    })}
                              />
                            ) : (
                              <UserAvatar
                                {...(user?.avatar
                                  ? { image: user?.avatar }
                                  : {
                                      icon: "user-alt",
                                    })}
                              />
                            )}
                          </>
                        )}
                        <div className="user-name">
                          {item.user ? (
                            <>
                              {item.user?.firstName} {item.user?.lastName}
                              <p className="fw-normal text-muted">ادمین</p>
                            </>
                          ) : (
                            <>
                              {auth?.isAdmin ? (
                                <>
                                  {ticket.user?.firstName}{" "}
                                  {ticket.user?.lastName}
                                  <p className="fw-normal text-muted">کاربر</p>
                                </>
                              ) : (
                                <>
                                  {user?.firstName} {user?.lastName}
                                  <p className="fw-normal text-muted">کاربر</p>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="date-time">
                        {convertDate(item.createdAt, "DD MMM YYYY - HH:mm")}
                      </div>
                    </div>
                    <div className="nk-reply-body">
                      <div className="nk-reply-entry entry">
                        <p className="pre_wrap-white_space">{item.message}</p>
                        {item.file && (
                          <a
                            href={hostname + item.file}
                            download
                            target="_blank"
                            without
                            rel="noreferrer"
                            className="btn btn-info"
                          >
                            دانلود فایل ضمیمه
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              <div className="nk-reply-form">
                <form
                  className="nk-reply-form-editor"
                  onSubmit={handleSendTicketMessage}
                >
                  <div className="nk-reply-form-field">
                    <textarea
                      className="form-control form-control-simple no-resize"
                      placeholder="متن پیام"
                      name="message"
                      id="message"
                      rows={7}
                    />
                  </div>
                  <div className="nk-reply-form-tools">
                    <ul className="nk-reply-form-actions g-1">
                      <li>
                        <Button color="primary" type="submit" loading={loader}>
                          ارسال
                        </Button>
                      </li>

                      <li>
                        <label htmlFor="attachment" className="m-0">
                          <Icon name="clip-v" className="fa-2x" />
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.gif,.docx,.xlsx,.csv,.pdf,.mp4,.avi;capture=camera"
                            id="attachment"
                            name="attachment"
                            className="d-none"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              handleUploadFile(
                                file,
                                (e) =>
                                  (document.getElementById(`clip`).value = e)
                              );
                              document.getElementById(`attachment`).value = "";
                            }}
                          />
                        </label>
                        <input type="hidden" id="clip" name="clip" />
                      </li>
                    </ul>
                  </div>
                </form>
              </div>
            </SimpleBar>
          </div>
        )}
      </div>

      <Modal
        isOpen={isShowModal}
        toggle={() => setIsShowModal(false)}
        className="modal-dialog-centered"
        size="xs"
      >
        <ModalBody>
          <span
            type="button"
            onClick={() => setIsShowModal(false)}
            className="close"
          >
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">ایجاد تیکت</h5>
            <br />
            <Form
              className="is-alter"
              onSubmit={handleSubmit(handleCreateTicket)}
            >
              <Row className="gy-4">
                {auth.isAdmin && (
                  <Col size="12">
                    <FormGroup>
                      <label className="form-label" htmlFor="user">
                        کاربر
                      </label>
                      <div className="form-control-wrap">
                        <Controller
                          control={control}
                          render={({ field: { onChange, onBlur } }) => (
                            <>
                              <input type="hidden" className="form-control" />
                              <AsyncSelect
                                openMenuOnFocus
                                cacheOptions
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="لطفا کاربر را انتخاب نمایید"
                                isLoading={loader}
                                onBlur={onBlur}
                                noOptionsMessage={() => (
                                  <p>کاربر ای برای نمایش یافت نشد</p>
                                )}
                                loadingMessage={() => (
                                  <p>لطفا منتظر بمانید ...</p>
                                )}
                                loadOptions={(val, callback) => {
                                  handleGetUsers(val, callback);
                                }}
                                defaultOptions
                                onChange={(e) => onChange(e.value)}
                              />
                            </>
                          )}
                          name="user"
                        />
                        <ErrorMessage message={errors.user?.message} />
                      </div>
                    </FormGroup>
                  </Col>
                )}
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="title">
                      عنوان
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="عنوان را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("title")}
                      />
                      <ErrorMessage message={errors.title?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="message">
                      پیام
                    </label>
                    <div className="form-control-wrap">
                      <textarea
                        type="text"
                        placeholder="پیام را وارد نمایید"
                        className="form-control-lg form-control"
                        rows={8}
                        {...register("message")}
                      />
                      <ErrorMessage message={errors.message?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="message">
                      فایل ضمیمه <Badge color="primary">اختیاری</Badge>
                    </label>
                    <div className="form-control-wrap">
                      <label
                        htmlFor="file"
                        className="btn btn-info btn-block shadow"
                      >
                        {watch("file") || "انتخاب فایل ضمیمه"}

                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.gif,.docx,.xlsx,.csv,.pdf,.mp4,.avi;capture=camera"
                          id="file"
                          className="d-none"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            handleUploadFile(file, (e) => setValue("file", e));
                            document.getElementById(`file`).value = "";
                          }}
                        />
                      </label>
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        ایجاد تیکت
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => setIsShowModal(false)}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TicketsUser;
